// Frontend Types for Bantuan Distribution System
// Note: These types match the API response (camelCase) after conversion by the client

export interface Recipient {
  id: number;
  bil: string;
  nama: string;
  noIc: string;
  noTelefon?: string;
  alamat?: string;
  institution: string;
  jurusan?: string;
  icStatus?: 'VALID' | 'IC_INVALID' | 'DOB_INVALID' | null;
  age?: number;
  dateOfBirth?: string;
  gender?: 'LELAKI' | 'PEREMPUAN' | 'TIDAK DIKETAHUI' | null;
  birthplaceCode?: string;
  birthState?: string;
  slotKod?: string;
  slotMasa?: string;
  tarikhSlot?: string;
  dun: 'PERMAS' | 'JOHOR JAYA';
  statusKehadiran: number;
  checkedInAt?: string;
  createdAt: string;
  updatedAt: string;
  // WAKIL / Representative fields
  wakilNama?: string;
  wakilNoIc?: string;
  wakilNoTel?: string;
  wakilAlamat?: string;
  wakilHubungan?: string;
}

export interface UploadLog {
  id: number;
  fileName: string;
  totalRows: number;
  importedRows: number;
  rejectedRows: number;
  isUndone?: boolean;
  undoneAt?: string;
  uploadedAt: string;
}

export interface AttendanceLog {
  id: number;
  recipientId?: number;
  bil: string;
  nama: string;
  noIc: string;
  noTelefon?: string;
  alamat?: string;
  institution: string;
  jurusan?: string;
  icStatus?: 'VALID' | 'IC_INVALID' | 'DOB_INVALID' | null;
  age?: number;
  dateOfBirth?: string;
  gender?: 'LELAKI' | 'PEREMPUAN' | 'TIDAK DIKETAHUI' | null;
  birthplaceCode?: string;
  birthState?: string;
  slotKod?: string;
  slotMasa?: string;
  tarikhSlot?: string;
  dun: 'PERMAS' | 'JOHOR JAYA';
  statusKehadiran: number;
  checkedInAt?: string;
  createdAt: string;
  updatedAt: string;
  wakilNama?: string;
  wakilNoIc?: string;
  wakilNoTel?: string;
  wakilAlamat?: string;
  wakilHubungan?: string;
  checkedInBy?: string;
  recipient?: Recipient;
}

export interface DashboardStats {
  total: number;
  checkedIn: number;
  notCheckedIn: number;
  percentage: number;
  institutions: Array<{ name: string; total: number; checkedIn: number }>;
  ageGroups: Array<{ label: string; total: number; checkedIn: number }>;
  gender: {
    lelaki: { label: string; total: number; checkedIn: number };
    perempuan: { label: string; total: number; checkedIn: number };
    tidakDiketahui: { label: string; total: number; checkedIn: number };
  };
  birthStates: Array<{ name: string; total: number; checkedIn: number }>;
  invalidIcCount: number;
  invalidIcRecords: Array<{
    id: number;
    bil: string;
    nama: string;
    noIc: string;
    reason: string;
  }>;
  dun: {
    permas: { total: number; checkedIn: number };
    johorJaya: { total: number; checkedIn: number };
  };
  recentAttendance: AttendanceLog[];
  uploadSummary: {
    totalUploads: number;
    totalImports: number;
  };
}

export interface InstitutionStats {
  totalStudents: number;
  totalInstitutions: number;
  studentsInTopList: number;
  topLimit: number;
  institutions: Array<{ name: string; total: number }>;
  ageGroups: Array<{ label: string; total: number }>;
  gender: {
    lelaki: { label: string; total: number };
    perempuan: { label: string; total: number };
    tidakDiketahui: { label: string; total: number };
  };
  birthStates: Array<{ name: string; total: number }>;
}

export interface PublicStats {
  total: number;
  checkedIn: number;
  notCheckedIn: number;
  institutions: Array<{ name: string; total: number }>;
  ageGroups: Array<{ label: string; total: number }>;
  dun: {
    permas: number;
    johorJaya: number;
  };
}

export interface RecipientsResponse {
  recipients: Recipient[];
  total: number;
  page: number;
  limit: number;
}

export interface AttendanceLogsResponse {
  logs: AttendanceLog[];
  total: number;
  page: number;
  limit: number;
}

export interface UploadLogsResponse {
  logs: UploadLog[];
}

export interface CSVRow {
  rowNum: number;
  bil?: string;
  nama?: string;
  no_ic?: string;
  no_telefon?: string;
  alamat?: string;
  institution?: string;
  age?: number;
  slot_kod?: string;
  slot_masa?: string;
  tarikh_slot?: string;
  dun?: string;
}

export interface HeaderMapping {
  csvHeader: string;
  targetField: string;
}

export interface RecipientField {
  key: string;
  name: string;
  required?: boolean;
  type: 'core' | 'custom';
  id?: number;
}

export interface CsvColumnMapping {
  columnKey: string;
  targetType: 'core' | 'custom' | 'skip';
  targetFieldKey: string;
}

export interface CsvImportRow {
  rowNum: number;
  values: Record<string, string>;
}

export interface CsvImportPayload {
  fileName: string;
  dun: string;
  rows: CsvImportRow[];
  mappings: CsvColumnMapping[];
  options: {
    useFirstRowAsHeaders: boolean;
    detectDuplicateContacts: boolean;
  };
}

export const REQUIRED_FIELDS = ['bil', 'nama', 'no_ic', 'institution'] as const;
export const OPTIONAL_FIELDS = ['no_telefon', 'alamat', 'age', 'dun', 'slot_kod', 'slot_masa', 'tarikh_slot'] as const;

export interface UploadResponse {
  success: boolean;
  uploadLogId?: number;
  totalRows: number;
  importedRows: number;
  updatedRows: number;
  rejectedRows: number;
  skippedBlankRows?: number;
  duplicateInFileRows?: number;
  duplicateExistingRows?: number;
  optionsUsed?: {
    useFirstRowAsHeaders?: boolean;
    detectDuplicateContacts?: boolean;
    maxRowsPerSet?: number;
  };
  errors: string[];
}

export interface UndoUploadResponse {
  success: boolean;
  uploadLogId: number;
  undoneInserted: number;
  undoneUpdated: number;
  skippedMissing: number;
  message: string;
}

export const DUNS = ['PERMAS', 'JOHOR JAYA'] as const;

export const HEADER_SUGGESTIONS: Record<string, string[]> = {
  bil: ['bil', 'bilangan', 'number', 'no_bil', 'num'],
  nama: ['nama', 'full_name', 'name', 'nama Penuh'],
  no_ic: ['no_ic', 'ic', 'ic_number', 'kad_pengenalan', 'no_kp', 'nric'],
  no_telefon: ['no_telefon', 'phone', 'mobile', 'phone_number', 'telefon', 'no_tel'],
  alamat: ['alamat', 'address'],
  institution: ['institution', 'institusi', 'universiti', 'university', 'ipt', 'kolej'],
  dun: ['dun', 'kawasan'],
  age: ['umur', 'age', 'usia'],
  slot_kod: ['slot_kod', 'slot kod', 'kod_slot', 'kod slot'],
  slot_masa: ['slot_masa', 'masa_slot', 'slot', 'sesi', 'session', 'waktu', 'time_slot'],
  tarikh_slot: ['tarikh_slot', 'tarikh slot', 'slot_date', 'date_slot', 'tarikh'],
};
